/**
 * The forecast, and how to say it.
 *
 * At Bandon the wind IS the golf course. Seventy-one degrees with eighteen out
 * of the north is a different property from the same temperature in ten out of
 * the south-west, and the number that decides what a man puts in his bag is the
 * gust rather than the average. So the app carries wind, gust and direction
 * everywhere it carries a temperature.
 *
 * Pure. Nothing here fetches anything — the network half lives in the API, and
 * this half is the part with rules in it worth testing.
 */

export interface ForecastDay {
  /** `YYYY-MM-DD`, in the tournament's own timezone. */
  date: string
  /** WMO code, as published by the source. Translated by `conditions`. */
  code: number
  tempMax: number
  tempMin: number
  windMax: number
  gustMax: number
  /** Degrees the wind is coming FROM. */
  windDir: number
  rainChance: number
}

export interface ForecastHour {
  /** `YYYY-MM-DDTHH:00`, local. */
  at: string
  code: number
  temp: number
  wind: number
  gust: number
  dir: number
}

export interface Forecast {
  /** When it was fetched, so a stale one can say so rather than pretending. */
  fetchedAt: number
  days: ForecastDay[]
  hours: ForecastHour[]
}

const POINTS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
]

/** The compass point a wind is blowing FROM: 320° is NW, not "mostly west". */
export const compass = (deg: number): string => POINTS[Math.round(deg / 22.5) % 16] ?? ''

/**
 * What the sky is doing, in the words the group uses.
 *
 * A code with no entry returns nothing at all rather than a guess. On a links
 * course "Overcast" and "Fog" are different plans for the afternoon, and a
 * confident wrong word about the weather is worse than a blank.
 */
const SKY: Record<number, string> = {
  0: 'Clear',
  1: 'Mostly clear',
  2: 'Partly cloudy',
  3: 'Overcast',
  45: 'Fog',
  48: 'Freezing fog',
  51: 'Light drizzle',
  53: 'Drizzle',
  55: 'Heavy drizzle',
  56: 'Freezing drizzle',
  57: 'Freezing drizzle',
  61: 'Light rain',
  63: 'Rain',
  65: 'Heavy rain',
  66: 'Freezing rain',
  67: 'Freezing rain',
  71: 'Light snow',
  73: 'Snow',
  75: 'Heavy snow',
  80: 'Showers',
  81: 'Showers',
  82: 'Heavy showers',
  95: 'Thunderstorms',
  96: 'Thunderstorms',
  99: 'Thunderstorms',
}

export const conditions = (code: number | undefined): string =>
  code === undefined ? '' : (SKY[code] ?? '')

/** The forecast for one day, or nothing if it is beyond what anybody can see. */
export const dayFor = (f: Forecast | undefined, date: string): ForecastDay | undefined =>
  f?.days.find((d) => d.date === date)

/**
 * The forecast for the hour a group tees off in.
 *
 * The hour it STARTS, never the nearest one: a 15:59 tee time is played in the
 * three o'clock wind, and rounding up would quote a gust from after the group
 * had already left the tee.
 */
export function hourFor(
  f: Forecast | undefined,
  date: string,
  teeTime: string,
): ForecastHour | undefined {
  const hh = teeTime.slice(0, 2)
  return f?.hours.find((h) => h.at === `${date}T${hh}:00`)
}

/** The whole day, for the section that is open. */
export function longWeather(day: ForecastDay | undefined): string {
  if (!day) return ''
  const sky = conditions(day.code)
  const gust =
    Math.round(day.gustMax) > Math.round(day.windMax) ? ` gusting ${Math.round(day.gustMax)}` : ''
  return [
    sky,
    `${Math.round(day.tempMax)}°/${Math.round(day.tempMin)}°`,
    `wind ${Math.round(day.windMax)}${gust} ${compass(day.windDir)}`,
    day.rainChance >= 20 ? `rain ${day.rainChance}%` : '',
  ]
    .filter(Boolean)
    .join(' · ')
}

/** "66° · wind 16 gusting 22 NW" — what it is doing when this group goes off. */
export function teeWeather(hour: ForecastHour | undefined): string {
  if (!hour) return ''
  const gust = Math.round(hour.gust) > Math.round(hour.wind) ? ` gusting ${Math.round(hour.gust)}` : ''
  return `${Math.round(hour.temp)}° · wind ${Math.round(hour.wind)}${gust} ${compass(hour.dir)}`
}
